import { PauseIcon, SparklesIcon, UserIcon } from '@heroicons/react/solid';
import { FaFacebookMessenger } from 'react-icons/fa6';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import { useChannelsT } from '../useChannelsT';
import { delay, tone } from '../../primitives';

/**
 * A conversation in the Page inbox where the owner replies after the agent,
 * and the agent stepping back in that conversation.
 */
export default function MessengerTakeover({
  customer,
  messages,
}: {
  customer: string;
  messages: { from: 'customer' | 'agent' | 'owner'; text: string }[];
}) {
  const { t } = useChannelsT();

  return (
    <div className='relative mx-auto max-w-[30rem] pb-2 sm:pb-16'>
      <div className='overflow-hidden rounded-[22px] bg-white text-start text-[#050505] shadow-[0_40px_90px_-30px_rgba(0,0,0,0.7)] ring-1 ring-white/10 dark:bg-[#242526] dark:text-[#e4e6eb]'>
        <div className='flex items-center gap-3 border-b border-black/[0.07] px-5 py-3.5 dark:border-white/[0.08]'>
          <span className='flex h-9 w-9 flex-none items-center justify-center rounded-full bg-[linear-gradient(140deg,#fbbf24,#d97706)] text-[14px] font-semibold text-white'>
            {customer.charAt(0)}
          </span>
          <span className='min-w-0 flex-1'>
            <span className='block truncate text-[14px] font-semibold'>
              {customer}
            </span>
            <span className='flex items-center gap-1 text-[11.5px] text-[#65676b] dark:text-[#b0b3b8]'>
              <FaFacebookMessenger className='h-3 w-3 text-[#0084FF]' />
              Messenger
            </span>
          </span>
          <span className='rounded-md bg-[#f0f2f5] px-2 py-1 text-[11px] font-semibold text-[#65676b] dark:bg-white/10 dark:text-[#b0b3b8]'>
            {t('Page inbox')}
          </span>
        </div>
        <div className='space-y-3 px-5 py-5 text-[13.5px] leading-[1.4]'>
          {messages.map(({ from, text }, index) =>
            from === 'customer' ? (
              <div key={index} className='flex justify-start'>
                <p className='max-w-[80%] rounded-[18px] bg-[#f0f0f0] px-3 py-2 dark:bg-[#3a3b3c]'>
                  {text}
                </p>
              </div>
            ) : (
              <div key={index} className='flex flex-col items-end gap-1'>
                <span
                  className={clsxm(
                    'flex items-center gap-1 text-[11px] font-semibold',
                    from === 'agent'
                      ? 'text-primary-600 dark:text-primary-300'
                      : 'text-[#65676b] dark:text-[#b0b3b8]',
                  )}
                >
                  {from === 'agent' ? (
                    <SparklesIcon className='h-3.5 w-3.5' />
                  ) : (
                    <UserIcon className='h-3.5 w-3.5' />
                  )}
                  {from === 'agent' ? t('Your AI agent') : t('You')}
                </span>
                <p className='max-w-[80%] rounded-[18px] bg-[#0866ff] px-3 py-2 text-white'>
                  {text}
                </p>
              </div>
            ),
          )}
        </div>
      </div>

      <div
        className={clsxm(
          styles.rv,
          'relative mt-4 flex items-start gap-3 rounded-2xl p-4 text-start shadow-[0_30px_60px_-24px_rgba(0,0,0,0.65)] sm:absolute sm:-bottom-3 sm:start-[-1.5rem] sm:mt-0 sm:w-[17rem]',
          tone.surface,
          tone.line,
        )}
        style={delay(500)}
      >
        <span className='flex h-9 w-9 flex-none items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300'>
          <PauseIcon className='h-5 w-5' />
        </span>
        <span className='min-w-0'>
          <span className={clsxm('block text-[13px] font-semibold', tone.ink)}>
            {t('Your agent steps back')}
          </span>
          <span className={clsxm('mt-0.5 block text-xs leading-5', tone.quiet)}>
            {t('In this conversation, until an hour after your last reply.')}
          </span>
        </span>
      </div>
    </div>
  );
}
